/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef } from "react";
import { Form, Pagination } from "react-bootstrap";
import IcomoonIcon from "@icons/IcomoonIcon";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import { getVisiblePages, getTotalItemView } from "../../utils/paginationUtils";

interface SntTablePagingProps {
  gotoPage: (page: number) => void;
  previousPage: () => void;
  nextPage: () => void;
  pageCount: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  pageIndex: number;
  pageOptions: any[];
  pageSize: number;
  setPageSize: (size: number) => void;
  isShowSelectPageSize: boolean;
  totalItems: any;
  headerGroups?: any[];
}

export const SntTablePaging = ({
  gotoPage,
  previousPage,
  nextPage,
  pageCount,
  canPreviousPage,
  canNextPage,
  pageIndex,
  pageOptions,
  pageSize,
  setPageSize,
  isShowSelectPageSize,
  totalItems,
}: SntTablePagingProps) => {
  const visiblePages = getVisiblePages(pageIndex, pageCount);
  const visibleTotal = getTotalItemView(totalItems, pageSize, pageIndex);

  return (
    <div className="clearfix">
      <div className="d-block d-md-inline-block float-md-start pt-2 mb-2">
        {totalItems?.isCountLoading ? (
          <span className="d-inline-flex align-items-center">
            <IcomoonIcon
              icon="spinner9"
              iconClassName="icon-spin me-2"
              color="#75b9e6"
              size={16}
            />
            {translate(LanguageKey.loading_count_key)}
          </span>
        ) : totalItems && totalItems.countByFilter > 0 ? (
          <span>
            {translate(LanguageKey.showing_key)}{" "}
            <strong>
              {visibleTotal.from} - {visibleTotal.to}
              {"/"}
              {totalItems.countByFilter} {translate(LanguageKey.items_key)}
            </strong>{" "}
          </span>
        ) : (
          <span>
            {translate(LanguageKey.page_key)}{" "}
            <strong>
              {pageOptions.length === 0 ? 0 : pageIndex + 1} of{" "}
              {pageOptions.length}
            </strong>{" "}
          </span>
        )}
      </div>

      <div className="d-block d-md-inline-block float-md-end mb-2">
        {isShowSelectPageSize && (
          <div className="d-inline-block me-3">
            <Form.Select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[25, 50, 100, 200].map((size) => (
                <option key={size} value={size}>
                  {translate(LanguageKey.show_key)} {size}
                </option>
              ))}
            </Form.Select>
          </div>
        )}

        <div className="d-inline-block float-end">
          <Pagination className="mb-0 align-self-center">
            <Pagination.Prev
              disabled={!canPreviousPage}
              onClick={() => canPreviousPage && previousPage()}
            />
            {visiblePages.map((page: any, index: any, array: any) => {
              if (array[index - 1] + 2 < page) {
                return (
                  <React.Fragment key={index}>
                    <Pagination.Ellipsis />
                    <Pagination.Item
                      active={pageIndex === page - 1}
                      onClick={() => gotoPage(page - 1)}
                    >
                      {page}
                    </Pagination.Item>
                  </React.Fragment>
                );
              } else {
                return (
                  <React.Fragment key={index}>
                    <Pagination.Item
                      active={pageIndex === page - 1}
                      onClick={() => gotoPage(page - 1)}
                    >
                      {page}
                    </Pagination.Item>
                  </React.Fragment>
                );
              }
            })}
            <Pagination.Next
              disabled={!canNextPage}
              onClick={() => canNextPage && nextPage()}
            />
          </Pagination>
        </div>
      </div>
    </div>
  );
};

export const SntTablePagingRef = forwardRef<HTMLDivElement, SntTablePagingProps>(
  (props, ref) => {
    return (
      <div ref={ref}>
        <SntTablePaging {...props} />
      </div>
    );
  }
);
SntTablePagingRef.displayName = "SntTablePagingRef";
